import { Link } from '@tanstack/react-router';
import { capturePosthogEvent, POSTHOG_EVENTS } from '~/common/posthogEvents';
import { type DataRequestV2, RequestType } from '~/common/types';
import { renderRequestStatus } from '~/pages/projectDetails/utilitiesV2/Table/columns';
import { deriveRequestScope, regionPositionLabel, type ScopedSibling } from '../../requestScope';

const typeLabels: Record<string, string> = {
  [RequestType.MapsRequested]: 'Maps',
  [RequestType.AsbuiltsRequested]: 'As-Builts',
};

interface Props {
  dataRequest: DataRequestV2;
}

// CG-749: surface sub-geometry splits to the fulfillment team — which region of the
// agency this request is tied to, and the sibling requests of the same split. Purely
// additive information: the project geometry shown/exported elsewhere is untouched.
// Data-driven rather than flag-gated: scoped requests only exist when routing was on
// at submission and must stay visible even if the flag is later flipped off.
export default function RequestScopeBanner({ dataRequest }: Props) {
  const scope = deriveRequestScope(dataRequest, dataRequest.siblings);
  if (!scope) return null;

  const agencyName = dataRequest.agency.agencyName;
  const position = regionPositionLabel(scope);
  // Lead with the sub-region's internal name when it has one; the count stays as context.
  const headline = dataRequest.subGeometry?.name ? `${dataRequest.subGeometry.name} (${position})` : position;

  const onSiblingClick = (sibling: ScopedSibling) => {
    capturePosthogEvent(POSTHOG_EVENTS.civilsearchSiblingRequestClicked, {
      request_id: dataRequest.id,
      sibling_request_id: sibling.id,
      agency_id: dataRequest.agencyId,
      project_id: dataRequest.project.id,
      sibling_region_number: sibling.regionNumber,
    });
  };

  return (
    <div
      className="mb-4 flex flex-wrap items-center gap-x-2 gap-y-1.5 rounded border border-slate-200 border-l-4 border-l-blue-500 bg-slate-50 px-3 py-2 text-sm text-slate-600"
      data-testid="request-scope-banner"
    >
      <span className="font-semibold text-slate-900">{headline}</span>
      <span>
        {scope.isCatchAll
          ? `— remaining ${agencyName} area not tied to a specific sub-region`
          : `— sub-region request for ${agencyName}`}
      </span>
      <span className="text-slate-400">·</span>
      {/* Includes same-region siblings too (the Maps/As-Builts twin), hence not "other regions". */}
      <span>Related requests:</span>
      {scope.siblings.map((sibling) => {
        const to = `/admin/civil-search/requests/${sibling.id}/general`;

        return (
          <Link
            key={sibling.id}
            to={to}
            className="rounded border border-slate-300 bg-white px-1.5 py-0.5 text-xs text-slate-700 hover:bg-slate-100"
            onClick={() => onSiblingClick(sibling)}
            data-testid={`request-scope-sibling-link-${sibling.id}`}
          >
            {sibling.agencySubGeometryName ?? `Region ${sibling.regionNumber}`}
            {sibling.isCatchAll ? ' (remaining)' : ''} · {typeLabels[sibling.type] ?? sibling.type} ·{' '}
            {renderRequestStatus(sibling.requestStatus).trigger}
          </Link>
        );
      })}
    </div>
  );
}
